/**
 * Customer Store
 * Zustand store for customer state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CustomerListItem, CustomerDetailResponse } from '@/types/api/customer';

/**
 * Customer filter state
 */
export interface CustomerFilters {
  search: string;
  categoryIds: number[];
  country: string; // Format: "CountryID-StateID-CityID,CountryID-StateID,CountryID"
  isActive: boolean | null;
}

/**
 * Customer pagination state
 */
export interface CustomerPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Customer sort state
 */
export interface CustomerSort {
  sortBy: number | null;
  sortOrder: 0 | 1; // 0=ASC, 1=DESC
}

/**
 * Customer store state
 */
export interface CustomerState {
  // Data
  customers: CustomerListItem[];
  currentCustomer: CustomerDetailResponse | null;

  // Pagination
  pagination: CustomerPagination;

  // Filters
  filters: CustomerFilters;

  // Sorting
  sort: CustomerSort;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Actions - Data
  setCustomers: (customers: CustomerListItem[]) => void;
  setCurrentCustomer: (customer: CustomerDetailResponse | null) => void;
  addCustomer: (customer: CustomerListItem) => void;
  updateCustomer: (customerId: number, customer: CustomerDetailResponse) => void;
  updateCustomerInList: (customerId: number, updates: Partial<CustomerListItem>) => void;
  removeCustomer: (customerId: number) => void;

  // Actions - Pagination
  setPagination: (pagination: Partial<CustomerPagination>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;

  // Actions - Filters
  setFilters: (filters: Partial<CustomerFilters>) => void;
  setSearch: (search: string) => void;
  setCategoryIds: (categoryIds: number[]) => void;
  setCountry: (country: string) => void;
  setIsActive: (isActive: boolean | null) => void;
  resetFilters: () => void;

  // Actions - Sorting
  setSort: (sort: Partial<CustomerSort>) => void;
  setSortBy: (sortBy: number | null) => void;
  setSortOrder: (sortOrder: 0 | 1) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  customers: [],
  currentCustomer: null,
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    categoryIds: [],
    country: '',
    isActive: null,
  },
  sort: {
    sortBy: null,
    sortOrder: 0 as 0 | 1,
  },
  isLoading: false,
  error: null,
};

/**
 * Customer Zustand store
 */
export const useCustomerStore = create<CustomerState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        // Data actions
        setCustomers: (customers) =>
          set({ customers }, false, 'customers/setCustomers'),

        setCurrentCustomer: (customer) =>
          set({ currentCustomer: customer }, false, 'customers/setCurrentCustomer'),

        addCustomer: (customer) =>
          set(
            (state) => ({ customers: [customer, ...state.customers] }),
            false,
            'customers/addCustomer'
          ),

        updateCustomer: (customerId, customer) =>
          set(
            (state) => ({
              customers: state.customers.map((c) =>
                c.customerId === customerId
                  ? {
                      ...c,
                      customerName: customer.name,
                      localTaxId: customer.localTaxId,
                      isActive: customer.isActive,
                    }
                  : c
              ),
              currentCustomer: state.currentCustomer?.customerId === customerId ? customer : state.currentCustomer,
            }),
            false,
            'customers/updateCustomer'
          ),

        updateCustomerInList: (customerId, updates) =>
          set(
            (state) => ({
              customers: state.customers.map((c) =>
                c.customerId === customerId ? { ...c, ...updates } : c
              ),
            }),
            false,
            'customers/updateCustomerInList'
          ),

        removeCustomer: (customerId) =>
          set(
            (state) => ({
              customers: state.customers.filter((c) => c.customerId !== customerId),
            }),
            false,
            'customers/removeCustomer'
          ),

        // Pagination actions
        setPagination: (pagination) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, ...pagination },
            }),
            false,
            'customers/setPagination'
          ),

        setPage: (page) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, page },
            }),
            false,
            'customers/setPage'
          ),

        setPageSize: (pageSize) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, pageSize, page: 1 },
            }),
            false,
            'customers/setPageSize'
          ),

        // Filter actions
        setFilters: (filters) =>
          set(
            (state) => ({
              filters: { ...state.filters, ...filters },
              pagination: { ...state.pagination, page: 1 }, // Reset to page 1
            }),
            false,
            'customers/setFilters'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'customers/setSearch'
          ),

        setCategoryIds: (categoryIds) =>
          set(
            (state) => ({
              filters: { ...state.filters, categoryIds },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'customers/setCategoryIds'
          ),

        setCountry: (country) =>
          set(
            (state) => ({
              filters: { ...state.filters, country },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'customers/setCountry'
          ),

        setIsActive: (isActive) =>
          set(
            (state) => ({
              filters: { ...state.filters, isActive },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'customers/setIsActive'
          ),

        resetFilters: () =>
          set(
            {
              filters: initialState.filters,
              pagination: { ...initialState.pagination },
            },
            false,
            'customers/resetFilters'
          ),

        // Sort actions
        setSort: (sort) =>
          set(
            (state) => ({
              sort: { ...state.sort, ...sort },
            }),
            false,
            'customers/setSort'
          ),

        setSortBy: (sortBy) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortBy },
            }),
            false,
            'customers/setSortBy'
          ),

        setSortOrder: (sortOrder) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortOrder },
            }),
            false,
            'customers/setSortOrder'
          ),

        // UI state actions
        setLoading: (isLoading) =>
          set({ isLoading }, false, 'customers/setLoading'),

        setError: (error) =>
          set({ error }, false, 'customers/setError'),

        // Reset action
        reset: () =>
          set(initialState, false, 'customers/reset'),
      }),
      {
        name: 'customer-storage',
        // Only persist filters, pagination settings, and sort
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
          sort: state.sort,
        }),
        // Merge persisted state with initial state, ensuring isActive defaults to true
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<CustomerState> | undefined;
          return {
            ...currentState,
            ...persisted,
            filters: {
              ...currentState.filters,
              ...persisted?.filters,
              // Always default isActive to true if it was null/undefined in storage
              isActive: persisted?.filters?.isActive ?? true,
            },
          };
        },
      }
    ),
    { name: 'CustomerStore' }
  )
);

/**
 * Selectors for common queries
 */

// Get filter query parameters for API
export const useCustomerQueryParams = () => {
  const { filters, pagination, sort } = useCustomerStore();

  // Build params object, only including defined values
  const params: Record<string, string | number | boolean | undefined> = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    sortOrder: sort.sortOrder,
  };

  // Add optional filters only if they have values
  if (filters.search && filters.search.trim()) {
    params.search = filters.search.trim();
  }

  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.categoryIds = filters.categoryIds.join(',');
  }

  if (filters.country && filters.country.trim()) {
    params.country = filters.country.trim();
  }

  // Convert isActive boolean to API format: 1=active, 2=inactive, 3=all
  if (filters.isActive === true) {
    params.isActive = 1;
  } else if (filters.isActive === false) {
    params.isActive = 2;
  } else {
    params.isActive = 3;
  }

  if (sort.sortBy !== null) {
    params.sortBy = sort.sortBy;
  }

  return params;
};

// Check if filters are applied
export const useHasActiveCustomerFilters = () => {
  const { filters } = useCustomerStore();

  return (
    filters.search !== '' ||
    filters.categoryIds.length > 0 ||
    filters.country !== '' ||
    filters.isActive !== null
  );
};
